import CryptoJS from "crypto-js";
import * as Sentry from "@sentry/react";

const MIN_KEY_LENGTH = 16;

function createEncryptor(opts: any) {
  if (typeof opts === "string") {
    opts = {
      key: opts,
      hmac: true,
      debug: false,
    };
  }
  const { key, hmac: verifyHmac = true, debug = false, reviver = null } = opts;

  if (!key || typeof key !== "string") {
    throw new Error("a string key must be specified");
  }
  if (key.length < MIN_KEY_LENGTH) {
    throw new Error(`key must be at least ${MIN_KEY_LENGTH} characters long`);
  }
  if (
    reviver !== null &&
    reviver !== undefined &&
    typeof reviver !== "function"
  ) {
    throw new Error("reviver must be a function");
  }

  // Derive a 32-byte key using SHA-256
  const cryptoKey = CryptoJS.SHA256(key);

  // Constant-time comparison to replace scmp
  function secureCompare(a: string, b: string) {
    if (a.length !== b.length) return false;
    let result = 0;
    for (let i = 0; i < a.length; i++) {
      result |= a.charCodeAt(i) ^ b.charCodeAt(i);
    }
    return result === 0;
  }

  // HMAC-SHA256 of text using the derived key
  function hmac(text: string, format = "hex") {
    const hmac = CryptoJS.HmacSHA256(text, cryptoKey);
    return format === "hex"
      ? hmac.toString(CryptoJS.enc.Hex)
      : hmac.toString(CryptoJS.enc.Base64);
  }

  // Encrypts an object to a string: [hmac (hex)][iv (hex)][ciphertext (base64)]
  function encrypt(obj: any) {
    const json = JSON.stringify(obj);

    // Generate 16-byte random IV
    const iv = CryptoJS.lib.WordArray.random(16);

    // Encrypt with AES-256-CBC
    const encrypted = CryptoJS.AES.encrypt(json, cryptoKey, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    const ciphertext = encrypted.ciphertext.toString(CryptoJS.enc.Base64);

    // Combine IV (hex) + ciphertext (base64)
    const ivHex = iv.toString(CryptoJS.enc.Hex);
    let result = ivHex + ciphertext;

    // Prepend HMAC if enabled
    if (verifyHmac) {
      const hmacValue = hmac(result, "hex");
      result = hmacValue + result;
    }

    return result;
  }

  // Decrypts the ciphertext and returns the original object or null on failure
  function decrypt(cipherText: string) {
    if (!cipherText) {
      return null;
    }
    try {
      let ivHex, encryptedJson, expectedHmac;

      if (verifyHmac) {
        // Extract HMAC (64 chars), IV (32 chars), and ciphertext
        expectedHmac = cipherText.substring(0, 64);
        cipherText = cipherText.substring(64);
        ivHex = cipherText.substring(0, 32);
        encryptedJson = cipherText.substring(32);

        // Verify HMAC
        const actualHmac = hmac(ivHex + encryptedJson, "hex");
        if (!secureCompare(actualHmac, expectedHmac)) {
          throw new Error("HMAC does not match");
        }
      } else {
        // Extract IV (32 chars) and ciphertext
        ivHex = cipherText.substring(0, 32);
        encryptedJson = cipherText.substring(32);
      }

      // Convert IV to WordArray
      const iv = CryptoJS.enc.Hex.parse(ivHex);

      // Decrypt
      const decrypted = CryptoJS.AES.decrypt(
        { ciphertext: CryptoJS.enc.Base64.parse(encryptedJson) } as any,
        cryptoKey,
        {
          iv: iv,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7,
        }
      );

      // Convert to UTF-8 and parse JSON
      const json = decrypted.toString(CryptoJS.enc.Utf8);
      if (!json) {
        throw new Error("Decryption failed: Empty plaintext");
      }

      return JSON.parse(json, reviver);
    } catch (e) {
      if (debug) {
        Sentry.captureException(e);
      }
      return null;
    }
  }

  return {
    encrypt,
    decrypt,
    hmac,
  };
}

export default createEncryptor;
export { createEncryptor };
